// Maps between the LDAP pages' row shape (the CMS `ldap2` spelling the forms have
// always used) and sync-api v2 (SM-API). Two sync types share it: LDAPS is one
// server in the appliance's list, LDAP is the single legacy configuration.
import {
  changedSecret,
  createSyncConfiguration,
  CreateSyncConfigurationInput,
  LdapAttributeMap,
  LdapProtocol,
  LdapServerProviderConfig,
  LdapServerSyncConfigInput,
  LdapServerType,
  LegacyLdapProviderConfig,
  maskedSecret,
  newSecret,
  SecretMapper,
  SyncConfigurationNode,
  SyncSummary,
  SyncType,
  updateSyncConfiguration,
  UpdateSyncConfigurationInput,
} from "../../../utils/api/SyncApi";
import { mergeConfigToInput, mergeNodeToConfig } from "./googleAzureSyncMapper";
import type { MergeConfig } from "../sis-integration/SisIntegration";

const SERVER_TYPE_TO_V2: Record<string, LdapServerType> = {
  ad: "ACTIVE_DIRECTORY",
  ol: "OPEN_LDAP",
  ed: "NOVELL_EDIRECTORY",
  custom: "CUSTOM",
};
const SERVER_TYPE_TO_LEGACY = Object.fromEntries(Object.entries(SERVER_TYPE_TO_V2).map(([k, v]) => [v, k]));

const PROTOCOL_TO_V2: Record<string, LdapProtocol> = { ldap: "LDAP", ldaps: "LDAPS" };
const PROTOCOL_TO_LEGACY = Object.fromEntries(Object.entries(PROTOCOL_TO_V2).map(([k, v]) => [v, k]));

/** The row shape LdapServers.js and LdapConfiguration.js read and write. */
export interface LdapServerRow {
  id?: string;
  enabled: boolean;
  sync_enabled: boolean;
  sync_ous: boolean;
  sync_by_dn: boolean;
  type: string;
  protocol?: string;
  hostname: string;
  port: number | string;
  basedn: string;
  ldapusername: string;
  ldappassword?: string;
  search_query: string;
  userownedgroupmembership: boolean;
  usernameattribute: string;
  managerattribute: string;
  emailattribute: string;
  userbase: string;
  usergroupattribute: string;
  userdnattribute: string;
  groupdnattribute: string;
  oudnattribute: string;
  ounameattribute: string;
  authenticateuserusingsupplieddn: boolean;
  groupbase: string;
  groupnameattribute: string;
  groupmembernameattribute: string;
  merge_config?: MergeConfig;
  /** Whether sync-api would accept an execute for this server right now. */
  can_execute: boolean;
  /** The server's part in the latest LDAP run, attached by the servers page. */
  runMember?: SyncSummary;
  runInFlight?: boolean;
}

type LdapConfig = LdapServerProviderConfig | LegacyLdapProviderConfig;

const attributesToRow = (a: LdapAttributeMap) => ({
  usernameattribute: a.username,
  managerattribute: a.manager,
  emailattribute: a.email,
  userbase: a.userBase,
  usergroupattribute: a.userGroup,
  userdnattribute: a.userDN,
  groupbase: a.groupBase,
  groupnameattribute: a.groupName,
  groupmembernameattribute: a.groupMemberName,
  groupdnattribute: a.groupDN,
  oudnattribute: a.ouDN,
  ounameattribute: a.ouName,
  userownedgroupmembership: a.userOwnedGroupMembership,
  authenticateuserusingsupplieddn: a.authenticateWithSuppliedDN,
});

const attributesFromRow = (row: LdapServerRow): LdapAttributeMap => ({
  username: row.usernameattribute ?? "",
  manager: row.managerattribute ?? "",
  email: row.emailattribute ?? "",
  userBase: row.userbase ?? "",
  userGroup: row.usergroupattribute ?? "",
  userDN: row.userdnattribute ?? "",
  groupBase: row.groupbase ?? "",
  groupName: row.groupnameattribute ?? "",
  groupMemberName: row.groupmembernameattribute ?? "",
  groupDN: row.groupdnattribute ?? "",
  ouDN: row.oudnattribute ?? "",
  ouName: row.ounameattribute ?? "",
  userOwnedGroupMembership: row.userownedgroupmembership ?? false,
  authenticateWithSuppliedDN: row.authenticateuserusingsupplieddn ?? false,
});

const emptyAttributes = attributesToRow({
  username: "",
  manager: "",
  email: "",
  userBase: "",
  userGroup: "",
  userDN: "",
  groupBase: "",
  groupName: "",
  groupMemberName: "",
  groupDN: "",
  ouDN: "",
  ouName: "",
  userOwnedGroupMembership: false,
  authenticateWithSuppliedDN: false,
});

/**
 * Map an LDAPS or LDAP node into the row shape. The attribute set is always the
 * resolved one sync-api serves; the bind password renders as the masked placeholder
 * when set, which the write mapper treats as "unchanged".
 */
export const mapNodeToLdapServer = (node: SyncConfigurationNode): LdapServerRow => {
  const config = node.config as LdapConfig | undefined;
  const protocol = config && "protocol" in config ? PROTOCOL_TO_LEGACY[config.protocol] ?? config.protocol : undefined;
  return {
    id: node.id,
    enabled: node.enabled,
    sync_enabled: config?.syncEnabled ?? false,
    sync_ous: config?.syncOrgUnits ?? false,
    sync_by_dn: config?.syncByDN ?? false,
    type: config ? SERVER_TYPE_TO_LEGACY[config.serverType] ?? config.serverType : "",
    protocol,
    hostname: config?.hostname ?? "",
    port: config?.port ?? "",
    basedn: config?.baseDN ?? "",
    ldapusername: config?.bindUsername ?? "",
    ldappassword: maskedSecret(config?.credential),
    search_query: config?.searchQuery ?? "",
    ...(config ? attributesToRow(config.attributes) : emptyAttributes),
    merge_config: mergeNodeToConfig(node.merge),
    can_execute: node.sync?.canExecute ?? false,
  };
};

// sync-api resolves the attribute set from the server type and refuses a supplied
// one for anything but CUSTOM, so the row's attributes only travel for Manual.
const buildLdapBlock = (row: LdapServerRow, secret: SecretMapper): LdapServerSyncConfigInput => ({
  serverType: SERVER_TYPE_TO_V2[row.type] ?? (row.type as LdapServerType),
  hostname: row.hostname,
  port: Number(row.port),
  baseDN: row.basedn,
  bindUsername: row.ldapusername,
  bindPassword: secret(row.ldappassword),
  searchQuery: row.search_query,
  syncEnabled: row.sync_enabled,
  syncOrgUnits: row.sync_ous,
  syncByDN: row.sync_by_dn,
  ...(row.type === "custom" ? { attributes: attributesFromRow(row) } : {}),
});

const buildLdapServerBlock = (row: LdapServerRow, secret: SecretMapper): LdapServerSyncConfigInput => ({
  ...buildLdapBlock(row, secret),
  protocol: PROTOCOL_TO_V2[row.protocol ?? ""] ?? (row.protocol as LdapProtocol),
});

// An LDAP configuration has no stored name: sync-api serves the hostname as one and
// refuses a name on a patch. Create requires one, so it is the hostname.

export const mapLdapServerToCreateInput = (row: LdapServerRow, merge: MergeConfig): CreateSyncConfigurationInput => ({
  syncType: SyncType.Ldaps,
  name: row.hostname,
  enabled: row.enabled,
  merge: mergeConfigToInput(merge),
  ldapServer: buildLdapServerBlock(row, newSecret),
});

export const mapLdapServerToUpdateInput = (row: LdapServerRow): UpdateSyncConfigurationInput => ({
  enabled: row.enabled,
  ldapServer: buildLdapServerBlock(row, changedSecret),
});

/**
 * Create (no id) or update (id) an LDAP server. Every server on the appliance
 * shares one merge configuration, so it is only sent on create, where the first
 * server's value becomes the shared one.
 */
export const saveLdapServer = async (row: LdapServerRow, merge: MergeConfig, id?: string): Promise<void> => {
  if (id) {
    await updateSyncConfiguration(id, mapLdapServerToUpdateInput(row));
  } else {
    await createSyncConfiguration(mapLdapServerToCreateInput(row, merge));
  }
};

export const mapLegacyLdapToCreateInput = (row: LdapServerRow, merge: MergeConfig): CreateSyncConfigurationInput => ({
  syncType: SyncType.Ldap,
  name: row.hostname,
  enabled: row.enabled,
  merge: mergeConfigToInput(merge),
  legacyLdap: buildLdapBlock(row, newSecret),
});

export const mapLegacyLdapToUpdateInput = (row: LdapServerRow, merge: MergeConfig): UpdateSyncConfigurationInput => ({
  enabled: row.enabled,
  merge: mergeConfigToInput(merge),
  legacyLdap: buildLdapBlock(row, changedSecret),
});

/** Create (no id) or update (id) the appliance's single legacy LDAP configuration. */
export const saveLegacyLdap = async (row: LdapServerRow, merge: MergeConfig, id?: string): Promise<void> => {
  if (id) {
    await updateSyncConfiguration(id, mapLegacyLdapToUpdateInput(row, merge));
  } else {
    await createSyncConfiguration(mapLegacyLdapToCreateInput(row, merge));
  }
};
